import { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { SearchCriteria, criteriaKey, parseCriteria } from './criteria';
import { fetchCount } from './api';

// The live counter of the Recherche modal ("23 documents disponibles").
//
// `GET /search/count` is limited to 120 calls per minute and account; the
// form changes on every keystroke, so:
//   - the call goes out ~400 ms after the last change (debounce), except the
//     first, which goes out when the modal opens;
//   - it is cached by criteria: returning to an already-counted form (the empty one
//     after "Effacer", for example) does not call again;
//   - a 429 waits for `Retry-After` and retries (see api.fetchCount); in the
//     meantime `throttled` is true and the panel says so.
//
// While recalculating, `count` keeps the previous number
// (`keepPreviousData`) and `isStale` marks it to dim it: a spinner in its
// place would make the panel's big number jump.

export const SEARCH_COUNT_KEY = 'SEARCH_COUNT';

const DEBOUNCE_MS = 400;
const CACHE_MS = 60 * 1000;

const useDebounced = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    if (Object.is(value, debounced)) return undefined;
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);

  return debounced;
};

export const useSearchCount = (criteria: SearchCriteria, enabled = true) => {
  const key = useDebounced(criteriaKey(criteria), DEBOUNCE_MS);
  const [throttled, setThrottled] = useState(false);

  const query = useQuery<number, Error>(
    [SEARCH_COUNT_KEY, key],
    () => {
      setThrottled(false);
      // The key is the query string of the criteria: the call is the key's,
      // even if the form has changed during the debounce.
      return fetchCount(parseCriteria(new URLSearchParams(key)), {
        onThrottled: () => setThrottled(true),
      }).finally(() => setThrottled(false));
    },
    {
      enabled,
      keepPreviousData: true,
      staleTime: CACHE_MS,
      cacheTime: CACHE_MS,
      retry: false,
    },
  );

  return {
    count: query.data,
    // There is a number on screen that no longer matches the form: either
    // because the call is on its way, or because the debounce has not released it yet.
    isStale:
      query.isFetching || key !== criteriaKey(criteria) || query.data === undefined,
    error: query.error,
    throttled,
  };
};
